import { ArrowLeft, ArrowRight } from 'lucide-react';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { updatePersonalInfo } from '../../redux/Slices/ResumeSlice';
import Button from '../Common/Button';

type Props = {
  step: number;
  totalSteps: number;
  stepName: string;
  prevStepName: string;
  onNext: () => void;
  onBack: () => void;
};

const ContactInfoForm: React.FC<Props> = ({
  step,
  totalSteps,
  stepName,
  prevStepName,
  onNext,
  onBack
}) => {
  const dispatch = useDispatch();

  const personalInfo = useSelector(
    (state: any) => state.root.resume.personalInfo
  );

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const MAX_LIMITS: Record<string, number> = {
    address: 50,
    city: 50,
    state: 20,
    country: 15,
    pincode: 6
  };
  const alphabetOnly = (value: string) => /^[a-zA-Z\s]*$/.test(value);
  const numbersOnly = (value: string) => /^[0-9]*$/.test(value);
  const isValidEmail = (email = '') =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const isValidIndianPhone = (phone = '') => {
    const normalized = phone.replace(/\s+/g, '');
    if (/^\+91[6-9]\d{9}$/.test(normalized)) return true;
    if (/^0[6-9]\d{9}$/.test(normalized)) return true;
    if (/^[6-9]\d{9}$/.test(normalized)) return true;
    return false;
  };

  const isValidLinkedinUrl = (url = '') => {
    if (!url.trim()) return true;
    return /^https?:\/\/(www\.)?linkedin\.com\/in\/[\w-]+\/?$/.test(url.trim());
  };

  const isValidGithubUrl = (url = '') => {
    if (!url.trim()) return true;
    return /^https?:\/\/(www\.)?github\.com\/[\w-]+\/?$/.test(url.trim());
  };

  const emailValid = isValidEmail(personalInfo.email);
  const phoneValid = isValidIndianPhone(personalInfo.phone);
  const linkedinValid = isValidLinkedinUrl(personalInfo.linkedinUrl);
  const githubValid = isValidGithubUrl(personalInfo.githubUrl);

  const isFormValid =
    personalInfo.email?.trim() &&
    personalInfo.phone?.trim() &&
    emailValid &&
    phoneValid;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Character limit check
    if (MAX_LIMITS[name] && value.length > MAX_LIMITS[name]) return;

    // Alphabets only for state and country
    if ((name === 'state' || name === 'country') && !alphabetOnly(value))
      return;

    // Numbers only for pincode
    if (name === 'pincode' && !numbersOnly(value)) return;

    dispatch(
      updatePersonalInfo({
        [name]: value
      })
    );
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = `+91${e.target.value}`;
    const formatted = value.startsWith('+') ? value : `+${value}`;
    dispatch(updatePersonalInfo({ phone: formatted }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTouched(prev => ({ ...prev, [e.target.name]: true }));
  };

  // Strip +91 prefix for display in the input
  const displayPhone = personalInfo.phone?.replace(/^\+91/, '') || '';

  const CharCount = ({
    value,
    max
  }: {
    value: string | undefined;
    max: number;
  }) => {
    const len = value?.length || 0;
    return (
      <span
        className={`text-xs ${len >= max ? 'text-red-500' : 'text-gray-400'}`}
      >
        {len}/{max}
      </span>
    );
  };

  return (
    <div className='w-full bg-gamma'>
      <div className='flex-1 p-10'>
        {/* header */}
        <div className='mb-4 flex justify-between'>
          <div>
            <p className='text-sm text-gray-500'>
              Step {step} of {totalSteps}
            </p>
            <h1 className='text-2xl font-semibold'>{stepName}</h1>
          </div>

          <div className='text-brand'>
            {Math.round(((step - 1) / totalSteps) * 100)} %
          </div>
        </div>

        <div className='mb-8 h-1 rounded bg-gray-200'>
          <div
            className='h-1 rounded bg-brand'
            style={{
              width: `${((step - 1) / totalSteps) * 100}%`
            }}
          />
        </div>

        {/* grid */}
        <div className='grid grid-cols-3 gap-6'>
          <div className='col-span-3 rounded-xl bg-white p-6 shadow-sm'>
            <h3 className='mb-6 font-semibold'>Contact Information</h3>

            {/* email */}
            <div className='mb-5'>
              <label className='text-sm font-medium'>
                Email Address <span className='text-red-500'>*</span>
              </label>

              <input
                name='email'
                value={personalInfo.email}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder='Enter email'
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none ${
                  touched.email && !emailValid
                    ? 'border-red-400 focus:border-red-400'
                    : 'focus:border-gray-300'
                }`}
              />

              {touched.email && !emailValid ? (
                <p className='mt-1 text-xs text-red-500'>
                  Please enter a valid email address
                </p>
              ) : (
                <p className='mt-1 text-xs text-gray-400'>
                  We recommend a professional email
                </p>
              )}
            </div>

            {/* phone */}
            <div className='mb-5'>
              <label className='text-sm font-medium'>
                Phone Number <span className='text-red-500'>*</span>{' '}
              </label>

              <div
                className={`mt-1 flex items-center overflow-hidden rounded-lg border ${
                  touched.phone && !phoneValid
                    ? 'border-red-400'
                    : 'border-gray-200'
                }`}
              >
                <span className='flex items-center border-r bg-gray-50 px-3 py-3 text-sm text-gray-600'>
                  +91
                </span>
                <input
                  name='phone'
                  value={displayPhone}
                  onChange={handlePhoneChange}
                  onBlur={handleBlur}
                  placeholder='Enter phone number'
                  className='w-full p-3 focus:outline-none'
                />
              </div>

              {touched.phone && !phoneValid ? (
                <p className='mt-1 text-xs text-red-500'>
                  Please enter a valid 10-digit Indian mobile number
                </p>
              ) : (
                <p className='mt-1 text-xs text-gray-400'>
                  Enter your 10-digit mobile number
                </p>
              )}
            </div>

            {/* linkedin */}
            <div className='mb-5'>
              <label className='text-sm font-medium'>
                LinkedIn Profile URL
              </label>

              <input
                name='linkedinUrl'
                value={personalInfo.linkedinUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder='Enter LinkedIn URL'
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none ${
                  touched.linkedinUrl && !linkedinValid
                    ? 'border-red-400 focus:border-red-400'
                    : 'focus:border-gray-300'
                }`}
              />
              {touched.linkedinUrl && !linkedinValid ? (
                <p className='mt-1 text-xs text-red-500'>
                  Please enter a valid LinkedIn URL (e.g.,
                  https://linkedin.com/in/your-profile)
                </p>
              ) : (
                <p className='mt-1 text-xs text-gray-400'>
                  e.g., https://www.linkedin.com/in/your-profile
                </p>
              )}
            </div>

            {/* github */}
            <div>
              <label className='text-sm font-medium'>GitHub Profile URL</label>

              <input
                name='githubUrl'
                value={personalInfo.githubUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder='Enter GitHub URL'
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none ${
                  touched.githubUrl && !githubValid
                    ? 'border-red-400 focus:border-red-400'
                    : 'focus:border-gray-300'
                }`}
              />

              {touched.githubUrl && !githubValid ? (
                <p className='mt-1 text-xs text-red-500'>
                  Please enter a valid GitHub URL (e.g.,
                  https://github.com/your-username)
                </p>
              ) : (
                <p className='mt-1 text-xs text-gray-400'>
                  e.g., https://www.github.com/your-username
                </p>
              )}
            </div>
          </div>
        </div>

        {/* location */}
        <div className='mt-6 rounded-xl bg-white p-6 shadow-sm'>
          <h3 className='mb-6 font-semibold'>Location Details</h3>

          <div className='grid grid-cols-3 gap-6'>
            <div className='col-span-2'>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>Address</label>
                <CharCount value={personalInfo.address} max={50} />
              </div>
              <input
                name='address'
                value={personalInfo.address}
                onChange={handleChange}
                maxLength={50}
                placeholder='Enter address'
                className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              />
            </div>

            <div>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>City</label>
                <CharCount value={personalInfo.city} max={50} />
              </div>
              <input
                name='city'
                value={personalInfo.city}
                onChange={handleChange}
                maxLength={50}
                placeholder='Enter city'
                className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              />
            </div>

            <div>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>State / Province</label>
                <CharCount value={personalInfo.state} max={20} />
              </div>
              <input
                name='state'
                value={personalInfo.state}
                onChange={handleChange}
                maxLength={20}
                placeholder='Enter state'
                className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              />
            </div>

            <div>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>Country</label>
                <CharCount value={personalInfo.country} max={15} />
              </div>
              <input
                name='country'
                value={personalInfo.country}
                onChange={handleChange}
                maxLength={15}
                placeholder='Enter country'
                className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              />
            </div>

            <div>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>Postal Code</label>
                <CharCount value={personalInfo.pincode} max={6} />
              </div>
              <input
                name='pincode'
                value={personalInfo.pincode}
                onChange={handleChange}
                maxLength={6}
                placeholder='Enter postal code'
                className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              />
            </div>
          </div>
        </div>

        {/* footer buttons */}
        <div className='mt-10 flex items-center justify-between'>
          <Button
            label={`Previous: ${prevStepName}`}
            handleClickBtn={onBack}
            variant='outlined'
          >
            <ArrowLeft size={16} />
          </Button>

          <Button
            label='Continue'
            handleClickBtn={onNext}
            variant='contained'
            disabled={!isFormValid}
            iconPosition='right'
          >
            <ArrowRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ContactInfoForm;
